import React from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';
import {bindActionCreators} from 'redux';
import {Redirect} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import setDisplayName from 'recompose/setDisplayName';
import lifecycle from 'recompose/lifecycle';
import withProps from 'recompose/withProps';
import compose from 'recompose/compose';
import includes from 'lodash/includes';
import {ReportTypes} from 'api/reports';
import {Button, Container, Grid, Header, Icon, Segment} from 'semantic-ui-react';
import * as ResourceActions from 'resources/actions';
import {updatePageNumberForResourceAction} from 'resources/actions';
import {getUserResourceActions} from 'pages/Profile/selectors';
import {toggleSortForResourceAction} from 'viewOptions/actions';
import {createResourcePageItemsSelector, createResourcePageSelector} from 'resources/selectors';
import {createResourceSortOptionsSelector, createResourceSortsSelector} from 'viewOptions/selectors';
import VisibleColumnsModal from 'components/common/VisibleColumnsModal';
import StatusModal from 'components/common/StatusModal';
import ItemExplorer from 'components/common/ItemExplorer';
import UserSearchPanel from './UserSearchPanel/UserSearchPanel';
import CreateUserModal from './CreateUserModal/CreateUserModal';
import VisibleColumnsCheckbox from './VisibleColumnsCheckbox';
import * as UserActions from './actions';
import {
  EmptyFunc,
  isFunction,
  ItemExplorerViewOptions,
  pageCount,
  prependRecentIfFirstPage,
  SortDirection,
  toggleSort
} from 'utils/DBUtils';

// returns a boolean representing whether filtering on `resourceType` returned any results.
const createSearchStatusSelector = resourceType => createSelector(
  state => createResourcePageItemsSelector(resourceType)(state.resources),
  searchResults => Boolean(searchResults.length)
);

const getSurveyAnswerReportActions = getUserResourceActions(ReportTypes.SurveyUserAnswerReport);

const mapStateToProps = (state, props) => {
  const getResourcePage = createResourcePageSelector(props.filterResource);
  const getPageItems = createResourcePageItemsSelector(props.filterResource);
  const getSorts = createResourceSortsSelector(props.filterResource);
  const getSortOptions = createResourceSortOptionsSelector(props.filterResource);
  const getSearchStatus = createSearchStatusSelector(props.filterResource);

  const page = getResourcePage(state.resources);
  const users = getPageItems(state.resources);
  const {
    pristine,
    creatingUser,
    createUserSubmitted,
    newUserId,
    searchingUsers,
    isVisibleColumnsModalOpen,
    tableColumnsInProgress,
      selectedUsers
  } = state.userManagement;

  return {
    pristine,
    selectedUsers,
    canExportSurveyData: includes(getSurveyAnswerReportActions(state), 'DOWNLOAD'),
    hasSearchResults: getSearchStatus(state),
    users: prependRecentIfFirstPage(page.page, page.count, [], users),
    sites: state.sites.resources,
    numOfItemsOnPage: users.length,
    count: pageCount(page.total, page.count),
    total: page.total,
    currentPage: page.page,
    sortOptions: getSortOptions(state),
    getSortDirectionForField: field => getSorts(state)[field] || SortDirection.Unsorted,
    creatingUser,
    createUserSubmitted,
    newUserId,
    searchingUsers,
    isVisibleColumnsModalOpen,
    tableColumnsInProgress
  };
};

const mapDispatchToProps = (dispatch, props) => bindActionCreators({

  openCreateUserModal: UserActions.startCreateUserAction,
  submitCreateUser: UserActions.submitCreateUserAction,
  closeCreateUserModal: UserActions.endCreateUserAction,

  saveVisibleColumns: UserActions.saveVisibleColumnsAction,
  updateVisibleColumns: UserActions.updateVisibleColumnsAction,
  openVisibleColumnsModal: () => UserActions.setVisibleColumnsModalAction(true),
  closeVisibleColumnsModal: () => UserActions.setVisibleColumnsModalAction(false),

  onPageSelected: page => props.updatePageNumberForResource(props.filterResource, page),
  onNextClicked: currentPage => props.updatePageNumberForResource(props.filterResource, currentPage + 1),
  onPreviousClicked: currentPage => props.updatePageNumberForResource(props.filterResource, currentPage - 1),
  selectSort: field => props.toggleSortForResource(props.filterResource, field),

  clearSearchResults: () => ResourceActions.clearResourcePageAction(props.filterResource)
}, dispatch);

const ListUsersTable = compose(
  withProps(props => ({
    showView: ItemExplorerViewOptions.List,
    itemsOnly: true,
    filterBarProps: {
      hidden: true
    },
    pagingBarProps: {
      count: props.count,
      total: props.total,
      currentPage: props.currentPage,
      maxPages: 10,
      numOfItemsOnPage: props.numOfItemsOnPage,
      onPageSelected: props.onPageSelected,
      onNextClicked: props.onNextClicked,
      onPreviousClicked: props.onPreviousClicked
    },
    tableProps: {
      celled: props.tableCelled,
      scrolling: props.tableScroll,
      collapsing: props.tableCollapse,
      items: props.users,
      onRowClicked: EmptyFunc,
      getSortDirectionForField: props.getSortDirectionForField,
      onSortClick: colDetail => {
        if (colDetail.fieldName) {
          props.selectSort(colDetail.fieldName);
        }
      },
      colDetails: props.colDetails
    }
  })),
  setDisplayName('ListUsersTable')
)(ItemExplorer);

const ListUsersPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentWillMount() {
      this.props.clearSearchResults();
    }
  }),
  setDisplayName('ListUsersPage')
)(props => props.newUserId ? <Redirect to={`${props.userMgmtConfig.detailViewPath}/${props.newUserId}`} /> : (
  <div className={props.topLevelClassName}>
      {props.hideTitle === true ? "" : (
          <Header as='h1'>
              <FormattedMessage id={props.pageHeaderKey} />
          </Header>
      )}
    <br/>
    <Grid>
      <Grid.Column width={props.searchPanelWidth}>
        <Segment>
          <Header as='h3'>
            <FormattedMessage id={props.searchHeaderKey} />
          </Header>
          <UserSearchPanel
            redirectUrl={props.listUsersPath}
            buildFilters={props.buildFilters}
            formConfig={props.searchFormConfig}
            filterResource={props.filterResource}
            sectionWrapper={props.sectionWrapper}
            clearButtonKey={props.clearButtonKey}
            allowClearQuery={props.allowClearQuery}
            searchButtonKey={props.searchButtonKey}
            showExportButton={props.searchFormExport} />
        </Segment>
      </Grid.Column>
      <Grid.Column width={props.tableWidth}>
        <Segment>
          <Grid className='users-list-top-matter'>
            <Grid.Column width={6} verticalAlign='middle'>
              <Header as='h3'>
                <FormattedMessage id={props.listHeaderKey} />
              </Header>
            </Grid.Column>
            <Grid.Column width={10} textAlign='right'>
              {props.topButtonConfigs.map(config => config.condition(props) && (
                <Button
                  primary
                  disabled={isFunction(config.disabled) && config.disabled(props)}
                  className={config.buttonClassName}
                  onClick={props[config.onClick]}>
                  {config.iconName ? (
                      <Icon
                          name={config.iconName}
                          className={config.iconClassName} />
                  ) : ""}
                  <FormattedMessage id={config.messageKey} />
                </Button>
              ))}
            </Grid.Column>
          </Grid>
          {props.searchingUsers ? (
            <Container textAlign='center'>
              <Icon loading name='spinner' size='huge' />
            </Container>
          ) : (props.hasSearchResults && !props.pristine) ? (
            <ListUsersTable {...props} />
          ) : (!props.pristine && (
            <Container>
              <Header as='h3' block textAlign='center'>
                <FormattedMessage id='noResults' />
              </Header>
            </Container>
          ))}
        </Segment>
      </Grid.Column>
    </Grid>
    <VisibleColumnsModal
      checkbox={VisibleColumnsCheckbox}
      columns={props.tableColumnsInProgress}
      handleSubmit={props.saveVisibleColumns}
      isOpen={props.isVisibleColumnsModalOpen}
      onValueChanged={props.updateVisibleColumns}
      handleClose={props.closeVisibleColumnsModal} />
    {props.creatingUser && (
      <CreateUserModal
        isOpen={props.creatingUser}
        onSaveData={props.submitCreateUser}
        onClose={props.closeCreateUserModal}
        isInFlight={props.createUserSubmitted} />
    )}
  </div>
));

export default ListUsersPage;



// WEBPACK FOOTER //
// ./public/pages/UserManagement/ListUsersPage.jsx